import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { Cart } from '@spartacus/cart/base/root';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { GenActiveCartService } from '../../cart/core/facade/active-cart.service';
import { GenOrderDocument } from 'src/app/generac/models/checkout.model';
import { GenOrderDocumentErrorService } from 'src/app/generac/services/order-document-error.service';

const MAX_DOCUMENTS = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const SUPPORTED_EXTENSIONS = new Set(['pdf', 'jpeg', 'jpg', 'png']);

@Component({
  selector: 'gen-builder-program-file-upload',
  templateUrl: './builder-program-file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenBuilderProgramFileUploadComponent implements OnDestroy {
  @Input() cart: Cart;
  @Input() set documents(value: GenOrderDocument[] | undefined) {
    this.uploadedDocuments = value ?? [];
    this.documentsChange.emit(this.uploadedDocuments);
  }
  @Output() documentsChange = new EventEmitter<GenOrderDocument[]>();

  readonly maxDocuments = MAX_DOCUMENTS;
  uploadedDocuments: GenOrderDocument[] = [];
  isDragging = false;
  isUploading = false;
  uploadProgress: number | null = null;
  removingDocumentCode: string | null = null;

  constructor(
    private checkoutService: GenCheckoutService,
    private activeCartService: GenActiveCartService,
    public documentErrorService: GenOrderDocumentErrorService,
    private cdr: ChangeDetectorRef,
  ) { }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.uploadFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.isUploading) {
      this.isDragging = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault(); this.isDragging = false;
    if (!this.isUploading) this.uploadFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  removeDocument(document: GenOrderDocument): void {
    if (this.isUploading || this.removingDocumentCode || !this.cart?.code) return;

    this.removingDocumentCode = document.code;
    this.checkoutService.removeOrderDocument(this.cart.code, document.code).subscribe({
      next: () => {
        this.uploadedDocuments = this.uploadedDocuments.filter((item) => item.code !== document.code);
        this.documentsChange.emit(this.uploadedDocuments);
        this.activeCartService.reloadActiveCart();
      },
      error: (error) => {
        this.removingDocumentCode = null;
        if (error?.status === 404 || error?.status === 409) {
          this.activeCartService.reloadActiveCart();
        }
        this.cdr.markForCheck();
      },
      complete: () => { this.removingDocumentCode = null; this.cdr.markForCheck(); },
    });
  }

  formatFileSize(size?: number): string {
    if (size === undefined || size === null) return '';
    return size < 1024 * 1024 ? `${Math.ceil(size / 1024)} KB` : `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  private uploadFiles(files: File[]): void {
    if (!files.length || this.isUploading || !this.cart?.code) return;
    this.documentErrorService.clear();
    if (!this.areFilesValid(files)) {
      this.documentErrorService.showUploadError();
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.cdr.markForCheck();
    this.checkoutService.uploadOrderDocuments(this.cart.code, files).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        if (event.type === HttpEventType.Response) {
          this.uploadedDocuments = event.body?.documents ?? this.uploadedDocuments;
          this.documentsChange.emit(this.uploadedDocuments);
          this.activeCartService.reloadActiveCart();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isUploading = false;
        this.uploadProgress = null;
        this.documentErrorService.showUploadError();
        this.cdr.markForCheck();
      },
      complete: () => { this.isUploading = false; this.uploadProgress = null; this.cdr.markForCheck(); },
    });
  }

  private areFilesValid(files: File[]): boolean {
    if (this.uploadedDocuments.length + files.length > MAX_DOCUMENTS) return false;
    return files.every((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      return file.size > 0 && file.size <= MAX_FILE_SIZE && SUPPORTED_EXTENSIONS.has(extension) && SUPPORTED_TYPES.has(file.type.toLowerCase());
    });
  }

  ngOnDestroy(): void {
    this.documentErrorService.clear();
  }
}
